import debounce from 'lodash.debounce'
import React, { useEffect } from 'react'
import ProductCard from '../../search-spring/components/ProductCard'
import useWindowSize from '../../search-spring/customHooks/useWindowSize'
import { Result } from '../../search-spring/types/types'

type Props = {
  products: Result[]
}

type DisrupterData = {
  button_alignment: string
  button_text: string
  button_url: string
  desktop_image: string
  heading: string
  middle_text: string
  mobile_image: string
  subheading: string
  tile_position: number
  video_url: string
}

declare global {
  interface Window {
    collectionDisrupterData1: DisrupterData
    collectionDisrupterData2: DisrupterData
    collectionDisrupterDesktopImage1: string
    collectionDisrupterMobileImage1: string
    collectionDisrupterDesktopImage2: string
    collectionDisrupterMobileImage2: string
  }
}

const returnButtonCenteringClasses = (position: string) => {
  switch (position) {
    case 'left':
      return 'justify-start pl-5'
    case 'center':
      return 'justify-center'
    case 'right':
      return 'justify-end pr-5'
    default:
      return 'justify-center'
  }
}

const returnDivCenteringClasses = (position: string) => {
  switch (position) {
    case 'left':
      return 'items-baseline pl-5 text-left'
    case 'center':
      return 'items-center text-center'
    case 'right':
      return 'items-end pr-5 text-right'
    default:
      return 'items-center'
  }
}

const CollectionSearchGrid = ({ products }: Props) => {
  if (!products || products.length === 0) return null

  const windowSize = useWindowSize()
  const isMobile = windowSize.width <= 641

  useEffect(() => {
    setTimeout(() => {
      const keySessionStorage = `scrollPos-${CD_STATE.searchspring.searchTerms}`
      window.addEventListener(
        'scroll',
        debounce(() => {
          sessionStorage.setItem(keySessionStorage, window.scrollY.toString())
        }, 200)
      )
      const scrollPos = parseInt(sessionStorage.getItem(keySessionStorage) || '0')

      let index = 0
      const interval = setInterval(() => {
        const scrollHeight = document.documentElement.scrollHeight
        if ((scrollPos && scrollPos < scrollHeight) || index > 5) {
          window.scrollTo({
            left: 0,
            top: scrollPos
          })
          clearInterval(interval)
        } else {
          clearInterval(interval)
        }
        index++
      }, 200)
    }, 500)
  }, [])
  const renderDisrupterTile = (data: DisrupterData, imageUrl: string, index: number) => {
    const divCenteringCSS = returnDivCenteringClasses(data.button_alignment)
    const buttonCenteringCSS = returnButtonCenteringClasses(data.button_alignment)
    const videoUrl = data?.video_url
    const isVideoMp4 = videoUrl ? videoUrl.toLowerCase().endsWith('.mp4') : false
    const rootUrlWithoutSlash = window.routes.rootUrlWithoutSlash
    const buttonUrl = data?.button_url
    const domain = window.routes.domain 
    const buttonUrlWithLocale = !rootUrlWithoutSlash || !buttonUrl
      ? buttonUrl ?? ''
      : buttonUrl.includes(rootUrlWithoutSlash)
      ? buttonUrl
      : buttonUrl.replace(domain, domain + rootUrlWithoutSlash)

    return (
      <div key={index} className="h-full col-span-2 row-span-4 pb-LG md:pb-0">
        <div className="disrupter-tile-container">
          <div className="relative">
            <a href={buttonUrlWithLocale || '#'}>
              <div className="absolute z-10 inset-0 gradient-overlay"></div>
              {videoUrl && isVideoMp4 ? (
                <div className="w-full aspect-[350/437] lg:aspect-[690/750]">
                  <video width="100%" height="100%" className="object-cover h-full w-full" playsInline autoPlay={true} loop muted>
                    <source src={videoUrl} type="video/mp4" />
                  </video>
                </div>
              ) : (
                <img src={imageUrl} alt="disrupter tile" className="w-full h-auto" />
              )}
            </a>
            <div className={`absolute z-10 inset-0 flex flex-col ${divCenteringCSS} mt-LG`}>
              {data?.subheading && <div className="font-bstSpyre uppercase text-sm text-white mb-2XS">{data.subheading}</div>}
              {data?.heading && <div className="font-bstSpyre uppercase text-base md:text-lg text-white">{data.heading}</div>}
            </div>
            {data?.middle_text && (
              <div className={`absolute inset-0 z-10 flex flex-col justify-center ${divCenteringCSS}`}>
                <div
                  className="font-bstSpyre capitalize text-lg-heading md:text-mdlg-heading text-white"
                  dangerouslySetInnerHTML={{ __html: data.middle_text }}
                ></div>
              </div>
            )}
            {buttonUrlWithLocale && data?.button_text && (
              <div className={`absolute bottom-LG z-10 flex w-full ${buttonCenteringCSS}`}>
                <a
                  href={buttonUrlWithLocale}
                  className={`text-white font-calibre text-sm-b min-w-[150px] uppercase rounded-sm backdrop-blur-[25px] bg-opacity-50 disrupter-button text-center ${
                    isMobile ? 'p-2.5' : 'p-SM'
                  }`}
                >
                  {data.button_text}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const GridHandler = (): JSX.Element => {
    const disrupterData1 = CD_STATE?.collectionDisrupter?.collectionDisrupterData1
    const disrupterData2 = CD_STATE?.collectionDisrupter?.collectionDisrupterData2

    return (
      <div className="grid custom-grid-styling auto-rows-auto gap-2.5 md:gap-5 px-5 mx-auto h-full relative overflow-visible">
        {products.map((product: Result, index: number) => {
          const tilePosition1 = isMobile && disrupterData1?.tile_position_mobile ? disrupterData1?.tile_position_mobile : disrupterData1?.tile_position
          const tilePosition2 = isMobile && disrupterData2?.tile_position_mobile ? disrupterData2?.tile_position_mobile : disrupterData2?.tile_position

          const disrupterTile =
            index === tilePosition1
              ? renderDisrupterTile(
                  disrupterData1,
                  isMobile ? CD_STATE.collectionDisrupter.collectionDisrupterMobileImage1 : CD_STATE.collectionDisrupter.collectionDisrupterDesktopImage1,
                  index
                )
              : index === tilePosition2
              ? renderDisrupterTile(
                  disrupterData2,
                  isMobile ? CD_STATE.collectionDisrupter.collectionDisrupterMobileImage2 : CD_STATE.collectionDisrupter.collectionDisrupterDesktopImage2,
                  index
                )
              : null

          return (
            <React.Fragment key={product.id}>
              {disrupterTile}
              <div className="w-full">
                <ProductCard product={product} />
              </div>
            </React.Fragment>
          )
        })}
      </div>
    )
  }

  return <GridHandler />
}

export default CollectionSearchGrid
